import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import FileUpload from '../../../components/ui/FileUpload';

const RoomsTab = ({ rooms, onAdd, onEdit, onDelete }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'residential',
    backgroundImage: '',
    backgroundImageAlt: '',
    dimensions: '',
    usageCount: 0
  });

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData(item);
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        category: 'residential',
        backgroundImage: '',
        backgroundImageAlt: '',
        dimensions: '',
        usageCount: 0
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (editingItem) {
      onEdit({ ...formData, id: editingItem?.id });
    } else {
      onAdd({ ...formData, id: Date.now() });
    }
    handleCloseModal();
  };

  const handleDelete = (item) => {
    if (window.confirm(`Delete "${item?.name}"? This will affect ${item?.usageCount} companies.`)) {
      onDelete(item?.id);
    }
  };

  const categoryRooms = {
    residential: rooms?.filter(r => r?.category === 'residential'),
    commercial: rooms?.filter(r => r?.category === 'commercial'),
    industrial: rooms?.filter(r => r?.category === 'industrial')
  };

  const renderRoomGrid = (categoryRooms, categoryName) => (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-text-primary capitalize">
        {categoryName} Rooms ({categoryRooms?.length})
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categoryRooms?.map((room) => (
          <div
            key={room?.id}
            className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200"
          >
            <div className="aspect-video bg-muted overflow-hidden">
              <Image
                src={room?.backgroundImage}
                alt={room?.backgroundImageAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4 space-y-3">
              <div>
                <h4 className="font-semibold text-text-primary">{room?.name}</h4>
                {room?.dimensions && (
                  <p className="text-sm text-text-secondary mt-1">{room?.dimensions}</p>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-text-secondary">
                <Icon name="Building2" size={14} />
                <span>Used by {room?.usageCount} companies</span>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenModal(room)}
                  iconName="Edit2"
                  iconPosition="left"
                  iconSize={14}
                  className="flex-1"
                >
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(room)}
                  iconName="Trash2"
                  iconSize={14}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Rooms</h2>
          <p className="text-sm text-text-secondary mt-1">
            Manage default room templates with background images
          </p>
        </div>
        <Button
          variant="default"
          onClick={() => handleOpenModal()}
          iconName="Plus"
          iconPosition="left"
          iconSize={16}
        >
          Add Room
        </Button>
      </div>
      <div className="space-y-8">
        {renderRoomGrid(categoryRooms?.residential, 'residential')}
        {renderRoomGrid(categoryRooms?.commercial, 'commercial')}
        {renderRoomGrid(categoryRooms?.industrial, 'industrial')}
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-primary">
                {editingItem ? 'Edit Room' : 'Add Room'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-text-secondary hover:text-text-primary transition-colors duration-150"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <Input
                label="Room Name"
                type="text"
                placeholder="e.g., Modern Kitchen"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                required
              />

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Category
                </label>
                <select
                  value={formData?.category}
                  onChange={(e) => setFormData({ ...formData, category: e?.target?.value })}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                >
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="industrial">Industrial</option>
                </select>
              </div>

              <Input
                label="Dimensions (Optional)"
                type="text"
                placeholder="e.g., 20x15 ft"
                value={formData?.dimensions}
                onChange={(e) => setFormData({ ...formData, dimensions: e?.target?.value })}
                description="Typical room dimensions for reference"
              />

              <FileUpload
                label="Background Image"
                accept="image/*"
                onUpload={(file) => {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setFormData({
                      ...formData,
                      backgroundImage: reader?.result,
                      backgroundImageAlt: `${formData?.name} interior view showing empty floor space ready for epoxy visualization`
                    });
                  };
                  reader?.readAsDataURL(file);
                }}
              />

              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" variant="default" className="flex-1">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomsTab;